import { useState, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api, { AdvanceCashRequest as ACType, advanceCashApi, ACSettlement, DealAttachment, ADMIN_ROLES, ApprovalEntry } from '@/lib/api'
import { formatDateOnly, formatMyanmarDateTime } from '@/lib/dateUtils'
import SlideOver from '@/components/SlideOver'
import CommentsSection from '@/components/CommentsSection'
import ApprovalsPanel from '@/components/ApprovalsPanel'
import {
  Plus, Search, AlertCircle, CheckCircle, Clock, XCircle,
  FileText, Image, Upload, X, Banknote, Trash2, FileDown
} from 'lucide-react'

const FINANCE_ADMIN = ['super_admin', 'admin', 'finance']

function statusBadge(status: string) {
  if (status === 'Approved') return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium"><CheckCircle size={12} />{status}</span>
  if (status === 'Rejected') return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium"><XCircle size={12} />{status}</span>
  if (status === 'Disbursed') return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 font-medium"><CheckCircle size={12} />{status}</span>
  return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium"><Clock size={12} />{status}</span>
}

function formatAmount(v: number) {
  if (!v) return '0 MMK'
  if (v >= 100000) return `${(v / 100000).toFixed(v % 100000 === 0 ? 0 : 1)} Lakhs MMK`
  return `${v.toLocaleString()} MMK`
}

function formatDate(d: string | null) {
  if (!d) return '—'
  return formatDateOnly(d) || d
}

function formatDateTime(d: string | null) {
  if (!d) return '—'
  return formatMyanmarDateTime(d) || d
}

export default function AdvanceCash() {
  const queryClient = useQueryClient()
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
  const userRole = currentUser.role || ''
  const isFinanceAdmin = FINANCE_ADMIN.includes(userRole)

  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const [selectedAC, setSelectedAC] = useState<ACType | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [tab, setTab] = useState<'details' | 'attachments' | 'comments'>('details')
  const [form, setForm] = useState<any>({})
  const [formError, setFormError] = useState('')
  const [formPhotos, setFormPhotos] = useState<File[]>([])
  const [formDocs, setFormDocs] = useState<File[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState('')
  const [rejectMode, setRejectMode] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [photoError, setPhotoError] = useState('')
  const [docError, setDocError] = useState('')
  const [showDisburse, setShowDisburse] = useState(false)
  const [disburseRef, setDisburseRef] = useState('')
  const photoRef = useRef<HTMLInputElement>(null)
  const docRef = useRef<HTMLInputElement>(null)
  const formPhotoRef = useRef<HTMLInputElement>(null)
  const formDocRef = useRef<HTMLInputElement>(null)

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) {
      api.get(`/advance-cash/${openId}`).then(r => {
        setSelectedAC(r.data)
        setSearchParams({}, { replace: true })
      }).catch(() => setSearchParams({}, { replace: true }))
    }
  }, [])

  const { data: acs = [], isLoading } = useQuery<ACType[]>({
    queryKey: ['advance-cash', statusFilter],
    queryFn: () => advanceCashApi.list({ status: statusFilter || undefined }).then(r => r.data),
  })

  const { data: acSummary } = useQuery({
    queryKey: ['advance-cash-summary'],
    queryFn: () => advanceCashApi.summary().then(r => r.data),
    enabled: isFinanceAdmin,
  })

  const { data: acsAllDisbursed = [] } = useQuery<ACType[]>({
    queryKey: ['advance-cash-disbursed-all'],
    queryFn: () => advanceCashApi.list({ status: 'Disbursed' }).then(r => r.data),
    enabled: isFinanceAdmin,
  })

  const { data: acSettlements = [] } = useQuery<ACSettlement[]>({
    queryKey: ['ac-settlements', selectedAC?.id],
    queryFn: () => advanceCashApi.settlements(selectedAC!.id).then(r => r.data),
    enabled: !!selectedAC && selectedAC.status === 'Disbursed',
  })

  const approveMut = useMutation({
    mutationFn: (id: number) => advanceCashApi.approve(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['advance-cash'] })
      setSelectedAC(prev => prev ? { ...prev, status: res.data.status, approvals: res.data.approvals } : prev)
    },
  })

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => advanceCashApi.reject(id, reason),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['advance-cash'] })
      setSelectedAC(prev => prev ? { ...prev, status: res.data.status, approvals: res.data.approvals } : prev)
      setRejectMode(false)
      setRejectReason('')
    },
  })

  const revokeMut = useMutation({
    mutationFn: (id: number) => advanceCashApi.revokeApproval(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['advance-cash'] })
      setSelectedAC(prev => prev ? { ...prev, status: res.data.status, approvals: res.data.approvals } : prev)
    },
  })

  const deletePhotoMut = useMutation({
    mutationFn: ({ id, photoId }: { id: number; photoId: string }) => advanceCashApi.deletePhoto(id, photoId),
    onSuccess: (_res, vars) => {
      setSelectedAC(prev => prev ? { ...prev, photos: (prev.photos || []).filter((p: any) => p.id !== vars.photoId) } : prev)
      queryClient.invalidateQueries({ queryKey: ['advance-cash'] })
    },
  })

  const deleteDocMut = useMutation({
    mutationFn: ({ id, docId }: { id: number; docId: string }) => advanceCashApi.deleteDocument(id, docId),
    onSuccess: (_res, vars) => {
      setSelectedAC(prev => prev ? { ...prev, documents: (prev.documents || []).filter((d: any) => d.id !== vars.docId) } : prev)
      queryClient.invalidateQueries({ queryKey: ['advance-cash'] })
    },
  })

  const deleteACMut = useMutation({
    mutationFn: (id: number) => advanceCashApi.deleteAC(id),
    onSuccess: () => {
      setSelectedAC(null)
      queryClient.invalidateQueries({ queryKey: ['advance-cash'] })
    },
  })

  const disburseMut = useMutation({
    mutationFn: ({ id, reference }: { id: number; reference?: string }) =>
      advanceCashApi.disburse(id, reference),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['advance-cash'] })
      queryClient.invalidateQueries({ queryKey: ['advance-cash-summary'] })
      setSelectedAC(prev => prev ? { ...prev, status: res.data.status, disbursed_at: res.data.disbursed_at } : prev)
      setShowDisburse(false)
      setDisburseRef('')
    },
  })

  function resetForm() {
    setForm({})
    setFormError('')
    setFormPhotos([])
    setFormDocs([])
    setSubmitStatus('')
    if (formPhotoRef.current) formPhotoRef.current.value = ''
    if (formDocRef.current) formDocRef.current.value = ''
  }

  function handleFormPhotoAdd(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || [])
    setFormPhotos(prev => [...prev, ...files].slice(0, 5))
    if (formPhotoRef.current) formPhotoRef.current.value = ''
  }

  function handleFormDocAdd(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || [])
    setFormDocs(prev => [...prev, ...files].slice(0, 5))
    if (formDocRef.current) formDocRef.current.value = ''
  }

  const filteredACs = (acs || []).filter(ac => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      ac.ac_number.toLowerCase().includes(q) ||
      ac.title.toLowerCase().includes(q) ||
      (ac.submitted_by_name || '').toLowerCase().includes(q)
    )
  })

  async function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError('')
    if (!form.title?.trim()) { setFormError('Please enter a title'); return }
    if (!form.amount || parseFloat(form.amount) <= 0) { setFormError('Please enter a valid amount'); return }
    setIsSubmitting(true)
    setSubmitStatus(formPhotos.length > 0 || formDocs.length > 0 ? 'Submitting request with attachments...' : 'Submitting request...')
    try {
      await advanceCashApi.create({
        title: form.title.trim(),
        amount: parseFloat(form.amount),
        additional_approvers: form.additional_approvers || [],
        requested_date: form.requested_date || null,
        notes: form.notes?.trim() || null,
        photos: formPhotos,
        documents: formDocs,
      })
      queryClient.invalidateQueries({ queryKey: ['advance-cash'] })
      queryClient.invalidateQueries({ queryKey: ['advance-cash-summary'] })
      setShowCreate(false)
      resetForm()
    } catch (err: any) {
      setFormError(err.response?.data?.detail || err.message || 'Failed to submit')
    } finally {
      setIsSubmitting(false)
      setSubmitStatus('')
    }
  }

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !selectedAC) return
    setPhotoError('')
    setUploadingPhoto(true)
    try {
      const res = await advanceCashApi.uploadPhoto(selectedAC.id, file)
      setSelectedAC(prev => prev ? { ...prev, photos: [...(prev.photos || []), res.data.file] } : prev)
      queryClient.invalidateQueries({ queryKey: ['advance-cash'] })
    } catch (err: any) {
      setPhotoError(err.response?.data?.detail || 'Failed to upload photo')
    } finally {
      setUploadingPhoto(false)
      if (photoRef.current) photoRef.current.value = ''
    }
  }

  async function handleDocUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !selectedAC) return
    setDocError('')
    setUploadingDoc(true)
    try {
      const res = await advanceCashApi.uploadDocument(selectedAC.id, file)
      setSelectedAC(prev => prev ? { ...prev, documents: [...(prev.documents || []), res.data.file] } : prev)
      queryClient.invalidateQueries({ queryKey: ['advance-cash'] })
    } catch (err: any) {
      setDocError(err.response?.data?.detail || 'Failed to upload document')
    } finally {
      setUploadingDoc(false)
      if (docRef.current) docRef.current.value = ''
    }
  }

  const canUpload = selectedAC
    ? (isFinanceAdmin || selectedAC.submitted_by === currentUser.id)
    : false

  const myDisbursedAcs = (acs || []).filter(ac => ac.submitted_by === currentUser.id && ac.status === 'Disbursed')
  const myDisbursedTotal = myDisbursedAcs.reduce((sum, ac) => sum + Number(ac.amount), 0)
  const mySettledTotal = myDisbursedAcs.reduce((sum, ac) => sum + Number(ac.settled_amount ?? 0), 0)
  const myRemainingTotal = myDisbursedTotal - mySettledTotal

  // Per-user balance groups for admin (always from unfiltered Disbursed ACs)
  const perUserBalances = (() => {
    if (!isFinanceAdmin) return []
    const map: Record<string, { id: string; name: string; disbursed: number; settled: number }> = {}
    for (const ac of acsAllDisbursed) {
      const key = String(ac.submitted_by ?? '')
      if (!map[key]) map[key] = { id: key, name: ac.submitted_by_name || '—', disbursed: 0, settled: 0 }
      map[key].disbursed += Number(ac.amount)
      map[key].settled += Number(ac.settled_amount ?? 0)
    }
    return Object.values(map).sort((a, b) => (b.disbursed - b.settled) - (a.disbursed - a.settled))
  })()

  const [showPerUser, setShowPerUser] = useState(true)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Banknote size={22} className="text-red-800" />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Advance Cash</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={async () => {
            try {
              const res = await api.get('/export/advance-cash', { params: { status: statusFilter, search }, responseType: 'blob' })
              const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = 'advance_cash.xlsx'; a.click(); URL.revokeObjectURL(url)
            } catch {}
          }} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600" title="Export to Excel">
            <FileDown size={15} /> <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => { resetForm(); setShowCreate(true) }}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900 min-h-[44px]"
          >
            <Plus size={16} /> New Request
          </button>
        </div>
      </div>

      {/* My Balance Strip (non-admin users with disbursed ACs) */}
      {!isFinanceAdmin && myDisbursedTotal > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total Disbursed', value: myDisbursedTotal, color: 'bg-teal-50 border-teal-200 text-teal-800' },
            { label: 'Settled', value: mySettledTotal, color: 'bg-blue-50 border-blue-200 text-blue-800' },
            { label: 'Remaining', value: myRemainingTotal, color: 'bg-orange-50 border-orange-200 text-orange-800' },
          ].map(card => (
            <div key={card.label} className={`rounded-xl border p-3 ${card.color}`}>
              <p className="text-xs font-medium opacity-80 mb-0.5">{card.label}</p>
              <p className="text-base sm:text-lg font-bold leading-tight">{formatAmount(card.value)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Finance Summary Strip */}
      {isFinanceAdmin && acSummary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Pending', count: acSummary.count_pending, total: acSummary.total_pending, color: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
            { label: 'Approved', count: acSummary.count_approved, total: acSummary.total_approved, color: 'bg-green-50 border-green-200 text-green-700' },
            { label: 'Disbursed', count: acSummary.count_disbursed, total: acSummary.total_disbursed, color: 'bg-teal-50 border-teal-200 text-teal-700' },
            { label: 'Rejected', count: acSummary.count_rejected, total: acSummary.total_rejected, color: 'bg-red-50 border-red-200 text-red-700' },
          ].map(s => (
            <div key={s.label} className={`rounded-xl border p-3 ${s.color}`}>
              <div className="text-xs font-medium opacity-80">{s.label}</div>
              <div className="text-lg font-bold mt-0.5">{s.count}</div>
              <div className="text-xs opacity-70">{formatAmount(s.total)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Per-User Balance Table (admin/finance only) */}
      {isFinanceAdmin && perUserBalances.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setShowPerUser(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <span>Per-User Outstanding Balances ({perUserBalances.length} {perUserBalances.length === 1 ? 'user' : 'users'})</span>
            <span className="text-slate-400 text-xs">{showPerUser ? '▲ collapse' : '▼ expand'}</span>
          </button>
          {showPerUser && (
            <div className="overflow-x-auto border-t border-slate-100">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Disbursed</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Settled</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Remaining</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {perUserBalances.map(row => {
                    const remaining = row.disbursed - row.settled
                    const pct = row.disbursed > 0 ? row.settled / row.disbursed : 0
                    const statusLabel = pct >= 1 ? 'Fully Settled' : pct > 0 ? 'Partially Settled' : 'Unsettled'
                    const statusColor = pct >= 1 ? 'bg-green-100 text-green-800' : pct > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800'
                    return (
                      <tr key={row.id} className="hover:bg-slate-50">
                        <td className="px-4 py-2.5 font-medium text-slate-900">{row.name}</td>
                        <td className="px-4 py-2.5 text-right text-slate-700">{formatAmount(row.disbursed)}</td>
                        <td className="px-4 py-2.5 text-right text-blue-700">{formatAmount(row.settled)}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-orange-700">{formatAmount(remaining)}</td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>{statusLabel}</span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
          <input
            type="text"
            placeholder="Search by number, title, name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
        >
          <option value="">All Statuses</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Disbursed">Disbursed</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-800" />
          </div>
        ) : filteredACs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-3">
            <Banknote size={40} strokeWidth={1} />
            <p className="text-sm">No advance cash requests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">AC #</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-600 text-xs uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Submitted By</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredACs.map(ac => (
                  <tr
                    key={ac.id}
                    onClick={() => { setSelectedAC(ac); setTab('details'); setRejectMode(false); setPhotoError(''); setDocError(''); setShowDisburse(false) }}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-red-900">{ac.ac_number}</td>
                    <td className="px-4 py-3 font-medium text-slate-900 max-w-[200px] truncate">{ac.title}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatAmount(Number(ac.amount))}</td>
                    <td className="px-4 py-3 text-slate-600">{ac.submitted_by_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-500">{formatDate(ac.requested_date || ac.created_at)}</td>
                    <td className="px-4 py-3">{statusBadge(ac.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create SlideOver */}
      <SlideOver open={showCreate} onClose={() => { setShowCreate(false); resetForm() }} title="New Advance Cash Request">
        <form onSubmit={handleFormSubmit} className="space-y-4">
          {formError && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200">
              <AlertCircle size={16} className="shrink-0" /><span>{formError}</span>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Title *</label>
            <input
              type="text"
              value={form.title || ''}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Purpose of advance cash"
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Amount (MMK) *</label>
            <input
              type="number"
              min="1"
              step="any"
              value={form.amount || ''}
              onChange={e => setForm({ ...form, amount: e.target.value })}
              placeholder="0"
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Requested Date</label>
            <input
              type="date"
              value={form.requested_date || ''}
              onChange={e => setForm({ ...form, requested_date: e.target.value })}
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Notes</label>
            <textarea
              value={form.notes || ''}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Additional details..."
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 resize-none"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-2">Additional Approvers</label>
            <div className="space-y-2">
              {['aungthanzaw', 'tuntunwin', 'thwinhtooaung'].map(username => {
                const selected: string[] = form.additional_approvers || []
                return (
                  <label key={username} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={selected.includes(username)}
                      onChange={e => {
                        const next = e.target.checked
                          ? [...selected, username]
                          : selected.filter(u => u !== username)
                        setForm({ ...form, additional_approvers: next })
                      }}
                      className="w-4 h-4 accent-red-800"
                    />
                    <span className="text-sm font-medium text-slate-800">{username}</span>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Form Photos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Photos (optional, max 5)</label>
              {formPhotos.length < 5 && (
                <label className="text-xs text-red-800 hover:text-red-900 font-medium flex items-center gap-1 cursor-pointer">
                  <Plus size={12} /> Add Photo
                  <input ref={formPhotoRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFormPhotoAdd} />
                </label>
              )}
            </div>
            {formPhotos.length > 0 && (
              <div className="space-y-1">
                {formPhotos.map((f, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200 text-sm">
                    <Image size={12} className="text-slate-400 shrink-0" />
                    <span className="flex-1 truncate text-slate-700 text-xs">{f.name}</span>
                    <button type="button" onClick={() => setFormPhotos(prev => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-600 shrink-0">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form Documents */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Documents (optional, max 5)</label>
              {formDocs.length < 5 && (
                <label className="text-xs text-red-800 hover:text-red-900 font-medium flex items-center gap-1 cursor-pointer">
                  <FileText size={12} /> Add Document
                  <input ref={formDocRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*" multiple className="hidden" onChange={handleFormDocAdd} />
                </label>
              )}
            </div>
            {formDocs.length > 0 && (
              <div className="space-y-1">
                {formDocs.map((f, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200 text-sm">
                    <FileText size={12} className="text-slate-400 shrink-0" />
                    <span className="flex-1 truncate text-slate-700 text-xs">{f.name}</span>
                    <button type="button" onClick={() => setFormDocs(prev => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-600 shrink-0">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {submitStatus && (
            <div className="flex items-center gap-2 bg-blue-50 text-blue-700 text-sm px-4 py-3 rounded-lg border border-blue-200">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-700 shrink-0" />
              <span>{submitStatus}</span>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={isSubmitting}
              className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
            <button type="button" onClick={() => { setShowCreate(false); resetForm() }} disabled={isSubmitting}
              className="py-2 px-4 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50">
              Cancel
            </button>
          </div>
        </form>
      </SlideOver>

      {/* Detail SlideOver */}
      <SlideOver
        open={!!selectedAC}
        onClose={() => { setSelectedAC(null); setRejectMode(false); setPhotoError(''); setDocError(''); setShowDisburse(false); setDisburseRef('') }}
        title={selectedAC ? `${selectedAC.ac_number} — ${selectedAC.title}` : ''}
        wide
      >
        {selectedAC && (
          <div className="space-y-4">
            {/* Status banner */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              {statusBadge(selectedAC.status)}
              <div className="flex items-center gap-2">
                {selectedAC.status === 'Approved' && isFinanceAdmin && !showDisburse && (
                  <button
                    onClick={() => setShowDisburse(true)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-teal-700 border border-teal-300 rounded-lg hover:bg-teal-50"
                  >
                    <CheckCircle size={13} /> Mark as Disbursed
                  </button>
                )}
                {selectedAC.status === 'Pending' && (isFinanceAdmin || selectedAC.submitted_by === currentUser.id) && (
                  <button
                    onClick={() => { if (confirm('Delete this advance cash request? This cannot be undone.')) deleteACMut.mutate(selectedAC.id) }}
                    disabled={deleteACMut.isPending}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 size={13} /> {deleteACMut.isPending ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </div>
            </div>

            {/* Disburse inline form */}
            {showDisburse && selectedAC.status === 'Approved' && isFinanceAdmin && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-teal-800">Mark as Disbursed</p>
                <input
                  type="text"
                  value={disburseRef}
                  onChange={e => setDisburseRef(e.target.value)}
                  placeholder="Reference / Voucher number (optional)"
                  className="w-full px-3 py-2 text-sm border border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => disburseMut.mutate({ id: selectedAC.id, reference: disburseRef || undefined })}
                    disabled={disburseMut.isPending}
                    className="flex-1 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                  >
                    {disburseMut.isPending ? 'Disbursing…' : 'Confirm Disbursement'}
                  </button>
                  <button
                    onClick={() => { setShowDisburse(false); setDisburseRef('') }}
                    className="py-2 px-4 text-sm bg-white border border-teal-200 text-teal-700 rounded-lg hover:bg-teal-50"
                  >
                    Cancel
                  </button>
                </div>
                {disburseMut.isError && (
                  <p className="text-xs text-red-600">{(disburseMut.error as any)?.response?.data?.detail || 'Failed to disburse'}</p>
                )}
              </div>
            )}

            {/* Approvals Panel */}
            <ApprovalsPanel
              approvals={selectedAC.approvals || []}
              additionalApprovers={selectedAC.additional_approvers || []}
              currentUsername={currentUser.username || ''}
              currentRole={userRole}
              isPending={selectedAC.status === 'Pending'}
              rejectMode={rejectMode}
              rejectReason={rejectReason}
              onRejectReasonChange={setRejectReason}
              onApprove={() => approveMut.mutate(selectedAC.id)}
              onReject={(reason) => rejectMut.mutate({ id: selectedAC.id, reason })}
              onRevoke={() => revokeMut.mutate(selectedAC.id)}
              onToggleRejectMode={() => setRejectMode(r => !r)}
              onCancelReject={() => { setRejectMode(false); setRejectReason('') }}
              isApprovePending={approveMut.isPending}
              isRejectPending={rejectMut.isPending}
              isRevokePending={revokeMut.isPending}
            />

            {/* Tabs */}
            <div className="flex gap-1 border-b border-slate-200">
              {(['details', 'attachments', 'comments'] as const).map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${tab === t ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500'}`}>
                  {t}
                </button>
              ))}
            </div>

            {tab === 'details' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['Amount', formatAmount(Number(selectedAC.amount))],
                    ['Requested Date', formatDate(selectedAC.requested_date)],
                    ['Submitted By', selectedAC.submitted_by_name || '—'],
                    ['Submitted At', formatDateTime(selectedAC.created_at)],
                    ['Additional Approvers', (selectedAC.additional_approvers || []).length > 0 ? (selectedAC.additional_approvers || []).join(', ') : '—'],
                  ].map(([label, value]) => (
                    <div key={label as string} className="text-sm">
                      <span className="text-slate-500 text-xs block">{label}</span>
                      <span className="text-slate-900 font-medium">{value}</span>
                    </div>
                  ))}
                </div>

                {selectedAC.disbursed_at && (
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                    <span className="text-teal-600 text-xs block font-medium mb-1">Disbursement Info</span>
                    <div className="flex flex-wrap gap-4">
                      <span className="text-xs text-slate-600">Date: <strong className="text-slate-800">{formatDateTime(selectedAC.disbursed_at)}</strong></span>
                      {selectedAC.disbursement_reference && (
                        <span className="text-xs text-slate-600">Ref: <strong className="text-slate-800">{selectedAC.disbursement_reference}</strong></span>
                      )}
                    </div>
                  </div>
                )}

                {selectedAC.notes && (
                  <div className="text-sm">
                    <span className="text-slate-500 text-xs block">Notes</span>
                    <p className="text-slate-700 whitespace-pre-wrap">{selectedAC.notes}</p>
                  </div>
                )}

                {/* Settlements Section */}
                {selectedAC.status === 'Disbursed' && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Expense Claim Settlements</h3>
                    {/* Balance Summary */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {[
                        { label: 'Disbursed', value: selectedAC.amount, color: 'bg-teal-50 border-teal-200 text-teal-800' },
                        { label: 'Settled', value: selectedAC.settled_amount ?? 0, color: 'bg-blue-50 border-blue-200 text-blue-800' },
                        { label: 'Remaining', value: selectedAC.amount - (selectedAC.settled_amount ?? 0), color: 'bg-orange-50 border-orange-200 text-orange-800' },
                      ].map(item => (
                        <div key={item.label} className={`rounded-lg border p-2 text-center ${item.color}`}>
                          <div className="text-xs font-medium mb-0.5">{item.label}</div>
                          <div className="text-xs font-bold">{item.value.toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                    {/* Settlement Status Badge */}
                    {selectedAC.settlement_status && (
                      <div className="mb-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                          selectedAC.settlement_status === 'Fully Settled' ? 'bg-green-100 text-green-800' :
                          selectedAC.settlement_status === 'Partially Settled' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {selectedAC.settlement_status}
                        </span>
                      </div>
                    )}
                    {/* Linked ECs */}
                    {acSettlements.length === 0 ? (
                      <p className="text-xs text-slate-400 text-center py-3">No expense claims submitted for settlement yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {acSettlements.map(ec => (
                          <div key={ec.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                            <div>
                              <span className="text-xs font-semibold text-slate-700">{ec.ec_number}</span>
                              <span className="text-xs text-slate-500 ml-2">{ec.title}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-slate-800">MMK {Number(ec.total_amount).toLocaleString()}</span>
                              <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                                ec.status === 'Approved' ? 'bg-green-100 text-green-800' :
                                ec.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>{ec.status}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {tab === 'attachments' && (
              <div className="space-y-5">
                {/* Photos */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Image size={14} /> Photos</h3>
                    {canUpload && (
                      <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 cursor-pointer">
                        <Upload size={12} /> {uploadingPhoto ? 'Uploading...' : 'Upload'}
                        <input ref={photoRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                      </label>
                    )}
                  </div>
                  {photoError && <p className="text-xs text-red-600 mb-2">{photoError}</p>}
                  {(selectedAC.photos || []).length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">No photos uploaded</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {(selectedAC.photos || []).map((photo: DealAttachment) => {
                        const src = advanceCashApi.getFileSrc(photo, selectedAC.id)
                        return (
                          <div key={photo.id} className="relative group rounded-lg overflow-hidden border border-slate-200 aspect-square bg-slate-100">
                            <a href={src} target="_blank" rel="noreferrer">
                              <img src={src} alt={photo.name} className="w-full h-full object-cover" />
                            </a>
                            {canUpload && (
                              <button
                                onClick={() => { if (confirm('Remove this photo?')) deletePhotoMut.mutate({ id: selectedAC.id, photoId: photo.id }) }}
                                className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              ><X size={10} /></button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Documents */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><FileText size={14} /> Documents</h3>
                    {canUpload && (
                      <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 cursor-pointer">
                        <Upload size={12} /> {uploadingDoc ? 'Uploading...' : 'Upload'}
                        <input ref={docRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*" className="hidden" onChange={handleDocUpload} disabled={uploadingDoc} />
                      </label>
                    )}
                  </div>
                  {docError && <p className="text-xs text-red-600 mb-2">{docError}</p>}
                  {(selectedAC.documents || []).length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">No documents uploaded</p>
                  ) : (
                    <div className="space-y-2">
                      {(selectedAC.documents || []).map((doc: DealAttachment) => {
                        const src = advanceCashApi.getFileSrc(doc, selectedAC.id)
                        return (
                          <div key={doc.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <FileText size={16} className="text-red-800 shrink-0" />
                            <a href={src} target="_blank" rel="noreferrer" className="flex-1 text-sm text-slate-700 hover:text-red-800 truncate">{doc.name}</a>
                            <span className="text-xs text-slate-400">{doc.size ? `${(doc.size / 1024).toFixed(0)} KB` : ''}</span>
                            {canUpload && (
                              <button
                                onClick={() => { if (confirm('Remove this document?')) deleteDocMut.mutate({ id: selectedAC.id, docId: doc.id }) }}
                                className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                              ><X size={14} /></button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'comments' && (
              <CommentsSection entityType="advance_cash_request" entityId={selectedAC.id} />
            )}
          </div>
        )}
      </SlideOver>
    </div>
  )
}
